import { useEffect, useMemo, useRef, useState } from "react"
import type { AccusationTrial } from "../types"
import { getSpeakerStyle } from "../game/sprites"

interface TrialScreenProps {
  trial: AccusationTrial
  /** The player answered wrongly; the guard is called and a battle begins. */
  onArrested: () => void
  /** Every step answered truthfully — the forgery is disproved. */
  onCleared: () => void
}

/**
 * The rival's slow game, faced. Aldric Vane has forged the player out of an
 * alliance; the accused court reads the "evidence" and the player must answer
 * each challenge correctly. One wrong answer ends the talking — the leader
 * calls the guard (`onArrested` → the household-guard battle). Answering every
 * step truthfully clears the name (`onCleared`).
 *
 * Unlike the linear EventScreen, this conversation branches on the player's
 * choices, so it is a screen of its own rather than a dialogue in the data.
 */
export function TrialScreen({ trial, onArrested, onCleared }: TrialScreenProps) {
  // "intro" → the scene; then each step in turn; "cleared" → the closing lines.
  type Phase =
    | { kind: "intro" }
    | { kind: "question"; step: number }
    | { kind: "reply"; step: number; answer: number; correct: boolean }
    | { kind: "cleared"; line: number }
  const [phase, setPhase] = useState<Phase>({ kind: "intro" })
  const primaryRef = useRef<HTMLButtonElement | null>(null)

  const speakerStyle = useMemo(() => getSpeakerStyle(trial.speaker), [trial.speaker])

  // Keep the primary action focused as the trial advances, so it stays
  // keyboard-actionable without a stray Tab — matching the EventScreen flow.
  useEffect(() => {
    primaryRef.current?.focus()
  }, [phase])

  function advanceFromReply(step: number) {
    // A truthful answer moves on; the last step's truth opens the clearing.
    if (step >= trial.steps.length - 1) {
      setPhase({ kind: "cleared", line: 0 })
    } else {
      setPhase({ kind: "question", step: step + 1 })
    }
  }

  function renderBody() {
    switch (phase.kind) {
      case "intro":
        return (
          <>
            <p className="event-line">{trial.intro}</p>
            <div className="event-actions">
              <button
                ref={primaryRef}
                type="button"
                onClick={() => setPhase({ kind: "question", step: 0 })}
              >
                Face the accusation
              </button>
            </div>
          </>
        )
      case "question": {
        const step = trial.steps[phase.step]
        return (
          <>
            <p key={phase.step} className="event-line">
              {step.prompt}
            </p>
            <ul className="trial-answers">
              {step.answers.map((answer, index) => (
                <li key={index}>
                  <button
                    ref={index === 0 ? primaryRef : undefined}
                    type="button"
                    className="trial-answer"
                    onClick={() =>
                      setPhase({
                        kind: "reply",
                        step: phase.step,
                        answer: index,
                        correct: answer.correct === true,
                      })
                    }
                  >
                    {answer.text}
                  </button>
                </li>
              ))}
            </ul>
            <p className="event-progress">
              <span className="event-progress-count">
                Question {phase.step + 1} / {trial.steps.length}
              </span>
              <span className="event-progress-hint" aria-hidden="true">
                One answer is the truth. The rest call the guard.
              </span>
            </p>
          </>
        )
      }
      case "reply": {
        const step = trial.steps[phase.step]
        const answer = step.answers[phase.answer]
        return (
          <>
            <p key={`${phase.step}-${phase.answer}`} className="event-line">
              {answer.reply}
            </p>
            <div className="event-actions">
              {phase.correct ? (
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={() => advanceFromReply(phase.step)}
                >
                  {phase.step >= trial.steps.length - 1 ? "Continue" : "Press on"}
                </button>
              ) : (
                <button ref={primaryRef} type="button" onClick={onArrested}>
                  The guard closes in
                </button>
              )}
            </div>
          </>
        )
      }
      case "cleared": {
        const isLast = phase.line >= trial.cleared.length - 1
        return (
          <>
            <p key={phase.line} className="event-line">
              {trial.cleared[phase.line]}
            </p>
            <div className="event-actions">
              <button
                ref={primaryRef}
                type="button"
                onClick={() =>
                  isLast ? onCleared() : setPhase({ kind: "cleared", line: phase.line + 1 })
                }
              >
                {isLast ? "Continue" : "Next"}
              </button>
            </div>
          </>
        )
      }
    }
  }

  return (
    <div className="screen event-screen trial-screen">
      <div className="event-speaker">
        {speakerStyle && (
          <span className="speaker-portrait" style={speakerStyle} role="img" aria-hidden="true" />
        )}
        <h1>{trial.speaker}</h1>
      </div>
      {renderBody()}
    </div>
  )
}
